import React from "react"
import { Box, Checkbox, DialogContentText, DialogTitle, FormControlLabel, Link } from "@carrotfertility/carotene-core"
import { ArrowSubmitButton, BackArrow, ReimbursementDialogFraming } from "../shared/layouts"
import { Progress } from "#/components/views/modal/Progress"
import { useStateMachine } from "#/components/context/stateMachine/StateMachineV2"
import { useReimbursementContext } from "#/components/context/reimbursement/GetReimbursedContext"
import { FormattedMessage, useIntl } from "react-intl"
import { DialogActionsBar } from "@carrotfertility/carotene-core-x"
import { JourneyTypes } from "#/components/get-reimbursed/steps/ExpenseJourneyTypes"
import { useTermsAndConditionsUrl } from "#/utils/hooks/legal-notice-urls"

export const AttestationObgynFertilityCare = (): JSX.Element => {
  const { send, back } = useStateMachine()
  const intl = useIntl()

  const { state, updateAttestationObgynVisitRelatedToEligibleServices, updateAttestationObgynVisitOnlyFertilityCare } =
    useReimbursementContext()

  const canSubmit = state.attestationObgynVisitRelatedToEligibleServices && state.attestationObgynVisitOnlyFertilityCare

  const termsAndConditionsUrl = useTermsAndConditionsUrl()

  const handleContinueClick = (): void => {
    send(state.kindOfJourney.value === JourneyTypes.IUI.toString() ? "IUI" : "")
  }

  const goBack = (): void => {
    updateAttestationObgynVisitRelatedToEligibleServices(null)
    updateAttestationObgynVisitOnlyFertilityCare(null)
    back()
  }

  return (
    <>
      <Progress
        numSteps={3}
        step={2}
        label={intl.formatMessage({ defaultMessage: "About This OB/GYN Expense" })}
        inactiveStepBackgroundColor="#DBE6EE"
      />
      <BackArrow onClick={goBack} />
      <ReimbursementDialogFraming>
        <DialogTitle>
          <FormattedMessage defaultMessage="Please confirm two things." />
        </DialogTitle>
        <Box sx={{ display: "flex", flexDirection: "column", gap: (theme) => theme.tokens.spacing.lg }}>
          <DialogContentText>
            <FormattedMessage
              defaultMessage="To be eligible, OB/GYN visits must be related to initial fertility testing and/or basic
               fertility interventions. If you haven’t already, please review the <link>eligible OB/GYN expenses</link>
               in your benefit guide."
              values={{
                link: (linkContent) => (
                  <Link target="_blank" href={"/your-benefit-guide/coverage/fertility-care"} color="inherit">
                    {linkContent}
                  </Link>
                )
              }}
            />
          </DialogContentText>
          <DialogContentText>
            <FormattedMessage
              defaultMessage="General OB/GYN visits not related to fertility are not covered. If you misrepresent the
              purpose of your visit, we may deny your expense. You may also lose access to Carrot for benefit misuse.
              For more information, please review our <link>terms and conditions</link>."
              values={{
                link: (linkContent) => (
                  <Link target="_blank" href={termsAndConditionsUrl} color="inherit">
                    {linkContent}
                  </Link>
                )
              }}
            />
          </DialogContentText>
          <DialogContentText>
            <FormattedMessage defaultMessage="If you cannot check both boxes below, please do not submit this expense." />
          </DialogContentText>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" }
            }}
          >
            <FormControlLabel
              label={
                <FormattedMessage
                  defaultMessage="This OB/GYN visit was related to eligible fertility services listed
                 in my benefit guide."
                />
              }
              control={
                <Checkbox
                  value="acknowledged"
                  required={true}
                  checked={state.attestationObgynVisitRelatedToEligibleServices}
                  onChange={(e) => {
                    updateAttestationObgynVisitRelatedToEligibleServices(e.target.checked)
                  }}
                  data-testid="checkbox-visit-related-to-eligible-services"
                />
              }
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" }
            }}
          >
            <FormControlLabel
              label={
                <FormattedMessage
                  defaultMessage="This visit did not include a general wellness exam, annual exam,
                pregnancy care after 10 weeks of gestation, or any care unrelated to fertility."
                />
              }
              control={
                <Checkbox
                  value="acknowledged"
                  required={true}
                  checked={state.attestationObgynVisitOnlyFertilityCare}
                  onChange={(e) => {
                    updateAttestationObgynVisitOnlyFertilityCare(e.target.checked)
                  }}
                  data-testid="checkbox-visit-only-fertility-care"
                />
              }
            />
          </Box>
        </Box>
      </ReimbursementDialogFraming>
      <DialogActionsBar
        primaryAction={
          <ArrowSubmitButton enabled={canSubmit} onClick={handleContinueClick}>
            <FormattedMessage defaultMessage="Continue" />
          </ArrowSubmitButton>
        }
      />
    </>
  )
}
